<script setup lang="ts">
import { Capacitor } from '@capacitor/core';
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import type { FavoriteGroup } from '@/@types/group';
import ModalSheet from '@/components/layout/ModalSheet.vue';
import BaseIcon from '@/components/ui/BaseIcon.vue';
import EmptyState from '@/components/ui/EmptyState.vue';
import ListGroup from '@/components/ui/ListGroup.vue';
import InfoContainer from '@/components/ui/InfoContainer.vue';
import ListItem from '@/components/ui/ListItem.vue';
import NetworkBadge from '@/components/ui/NetworkBadge.vue';
import RoundedButton from '@/components/ui/RoundedButton.vue';
import Switch from '@/components/ui/Switch.vue';
import { AppRoute } from '@/libs/routing';
import { useFavoriteGroupsStore } from '@/stores/favorite-groups';
import { useFavoriteNotificationsStore } from '@/stores/favorite-notifications';
import { useGroupStore } from '@/stores/group';

const open = defineModel<boolean>('open', { required: true });

// Width of the "remove" action a swipe uncovers, and how far a swipe must go to leave it open.
const SWIPE_ACTION_WIDTH = 88;
const SWIPE_OPEN_THRESHOLD = SWIPE_ACTION_WIDTH / 2;

const router = useRouter();
const groupStore = useGroupStore();
const favoriteGroupsStore = useFavoriteGroupsStore();
const notificationsStore = useFavoriteNotificationsStore();

// Push only exists in the native app: on the web the notification controls would do nothing.
const isNative = Capacitor.isNativePlatform();

const isEditing = ref(false);
const swipedGroupId = ref<string>();
const swipeOffset = ref(0);
let swipeStart: { x: number; y: number; groupId: string } | undefined;

const displayedGroupId = computed<string | undefined>(() => groupStore.group?.group_id);

// The displayed network leads the list; it may not be a favorite (opened from a link).
const displayedGroup = computed<FavoriteGroup | undefined>(() => {
  const group = groupStore.group;
  if (!group) return undefined;
  return (
    favoriteGroupsStore.favorites.find(favorite => favorite.groupId === group.group_id) ?? {
      groupId: group.group_id,
      name: group.name,
      color: group.color,
      logoUrl: group.logo_url,
      notificationsEnabled: false,
    }
  );
});

const otherGroups = computed<FavoriteGroup[]>(() =>
  favoriteGroupsStore.favorites.filter(favorite => favorite.groupId !== displayedGroupId.value),
);

const rows = computed<FavoriteGroup[]>(() =>
  displayedGroup.value ? [displayedGroup.value, ...otherGroups.value] : otherGroups.value,
);

watch(open, isOpen => {
  if (isOpen) return;
  isEditing.value = false;
  swipedGroupId.value = undefined;
});

function closeThen(navigate: () => void) {
  open.value = false;
  navigate();
}

function selectGroup(groupId: string) {
  if (isEditing.value) return;
  if (swipedGroupId.value) {
    swipedGroupId.value = undefined;
    return;
  }
  closeThen(() => {
    if (groupId !== displayedGroupId.value) router.push({ name: AppRoute.MAP, params: { groupId } });
  });
}

function addGroup() {
  closeThen(() => router.push({ name: AppRoute.SELECT_GROUP }));
}

function removeGroup(groupId: string) {
  const index = favoriteGroupsStore.favorites.findIndex(favorite => favorite.groupId === groupId);
  favoriteGroupsStore.remove(groupId);
  swipedGroupId.value = undefined;
  if (groupId !== displayedGroupId.value) return;

  // Else it stays saved until the next network loads: an offline relaunch would show it again and re-add it as a favorite.
  localStorage.removeItem('group');
  const favorites = favoriteGroupsStore.favorites;
  // The one that followed it in the order of addition, the first one if it was the last.
  const next = favorites[index] ?? favorites[0];
  // replace, not push: Back must not reopen the removed network, which would re-add it as a favorite.
  if (next) return closeThen(() => router.replace({ name: AppRoute.MAP, params: { groupId: next.groupId } }));

  closeThen(() => {
    groupStore.group = null;
    router.replace({ name: AppRoute.SELECT_GROUP });
  });
}

function isFavorite(groupId: string): boolean {
  return favoriteGroupsStore.has(groupId);
}

function isNotified(group: FavoriteGroup): boolean {
  return group.notificationsEnabled && !notificationsStore.isBlocked;
}

// #region Swipe to remove
function onTouchStart(event: TouchEvent, groupId: string) {
  if (isEditing.value) return;
  const { clientX: x, clientY: y } = event.touches[0];
  swipeStart = { x, y, groupId };
  swipeOffset.value = swipedGroupId.value === groupId ? -SWIPE_ACTION_WIDTH : 0;
}

function onTouchMove(event: TouchEvent) {
  if (!swipeStart) return;
  const dx = event.touches[0].clientX - swipeStart.x;
  const dy = event.touches[0].clientY - swipeStart.y;
  // A vertical gesture scrolls the list or pulls the sheet: it is not ours.
  if (Math.abs(dy) > Math.abs(dx)) return;
  const base = swipedGroupId.value === swipeStart.groupId ? -SWIPE_ACTION_WIDTH : 0;
  swipedGroupId.value = swipeStart.groupId;
  swipeOffset.value = Math.min(0, Math.max(-SWIPE_ACTION_WIDTH, base + dx));
}

function onTouchEnd() {
  if (!swipeStart) return;
  const isOpened = swipeOffset.value <= -SWIPE_OPEN_THRESHOLD;
  swipedGroupId.value = isOpened ? swipeStart.groupId : undefined;
  swipeOffset.value = 0;
  swipeStart = undefined;
}

function rowOffset(groupId: string): number {
  if (swipedGroupId.value !== groupId) return 0;
  return swipeStart ? swipeOffset.value : -SWIPE_ACTION_WIDTH;
}
// #endregion
</script>

<template>
  <ModalSheet v-model:open="open" :label="$t('favoriteGroups.title')">
    <template #header>
      <div class="favorite-groups-sheet__header">
        <h2 class="favorite-groups-sheet__title">{{ $t('favoriteGroups.title') }}</h2>
        <button
          v-if="rows.length > 0"
          class="favorite-groups-sheet__edit"
          type="button"
          @click="isEditing = !isEditing"
        >
          {{ isEditing ? $t('favoriteGroups.done') : $t('favoriteGroups.edit') }}
        </button>
      </div>
    </template>

    <InfoContainer
      v-if="isNative && notificationsStore.isBlocked"
      :expandable="false"
      :title="$t('favoriteGroups.notificationsBlocked')"
      :subtitle="$t('favoriteGroups.notificationsBlockedHelp')"
      data-test="notifications-blocked"
    />

    <EmptyState v-if="rows.length === 0" :title="$t('favoriteGroups.empty')" />

    <ListGroup v-else>
      <div
        v-for="group in rows"
        :key="group.groupId"
        class="favorite-groups-sheet__row"
        :class="{ 'favorite-groups-sheet__row--displayed': group.groupId === displayedGroupId }"
        data-test="network-row"
      >
        <button
          class="favorite-groups-sheet__remove-swipe"
          type="button"
          :aria-hidden="swipedGroupId !== group.groupId"
          :aria-label="$t('favoriteGroups.remove', { name: group.name })"
          :style="{ width: SWIPE_ACTION_WIDTH + 'px' }"
          :tabindex="swipedGroupId === group.groupId ? 0 : -1"
          data-test="swipe-remove"
          @click="removeGroup(group.groupId)"
        >
          <BaseIcon name="trash-2" />
        </button>
        <div
          class="favorite-groups-sheet__row-content"
          :class="{
            'favorite-groups-sheet__row-content--dragging':
              swipedGroupId === group.groupId && swipeOffset !== 0,
          }"
          :style="{ transform: `translateX(${rowOffset(group.groupId)}px)` }"
          @touchstart.passive="onTouchStart($event, group.groupId)"
          @touchmove.passive="onTouchMove"
          @touchend="onTouchEnd"
          @touchcancel="onTouchEnd"
        >
          <button class="favorite-groups-sheet__select" type="button" @click="selectGroup(group.groupId)">
            <NetworkBadge :name="group.name" :color="group.color" :logo-url="group.logoUrl" />
            <ListItem class="favorite-groups-sheet__item" :label="group.name">
              <template v-if="group.groupId === displayedGroupId" #badges>
                <span class="favorite-groups-sheet__displayed-label">
                  {{ $t('favoriteGroups.displayed') }}
                </span>
              </template>
              <template v-if="group.groupId === displayedGroupId && !isEditing" #trailing>
                <BaseIcon name="check" class="favorite-groups-sheet__check" />
              </template>
            </ListItem>
          </button>
          <span
            v-if="isNative && isFavorite(group.groupId) && !isEditing"
            class="favorite-groups-sheet__notifications"
          >
            <BaseIcon :name="isNotified(group) ? 'bell' : 'bell-off'" />
            <Switch
              :model-value="isNotified(group)"
              :disabled="notificationsStore.isBlocked"
              :label="$t('favoriteGroups.notifications', { name: group.name })"
              data-test="notifications-switch"
              @update:modelValue="notificationsStore.setNotificationsEnabled(group.groupId, $event)"
            />
          </span>
          <RoundedButton
            v-if="isEditing"
            class="favorite-groups-sheet__remove"
            icon="trash-2"
            type="ghost"
            :aria-label="$t('favoriteGroups.remove', { name: group.name })"
            data-test="visible-remove"
            @click="removeGroup(group.groupId)"
          />
        </div>
      </div>
    </ListGroup>

    <button class="favorite-groups-sheet__add" type="button" data-test="add-network" @click="addGroup">
      <ListItem type="recent-search" icon="plus" :label="$t('favoriteGroups.add')" />
    </button>
  </ModalSheet>
</template>

<style lang="scss">
.favorite-groups-sheet {
  &__header {
    align-items: center;
    display: flex;
    justify-content: space-between;
    padding: $spacing-3 $spacing-5;
  }

  &__title {
    color: $color-neutral-highest;
    font: $heading-h4;
    letter-spacing: $heading-h4-spacing;
    margin: 0;
  }

  &__edit {
    background: none;
    border: none;
    color: $color-neutral-highest;
    cursor: pointer;
    font: $label-medium;
    letter-spacing: $label-medium-spacing;
    padding: $spacing-2;
  }

  &__row {
    overflow: hidden;
    position: relative;

    &--displayed {
      background-color: $color-primary-brand-lowest;

      .favorite-groups-sheet__row-content {
        background-color: $color-primary-brand-lowest;
      }
    }
  }

  &__row-content {
    align-items: center;
    background-color: $color-white;
    display: flex;
    padding-left: $spacing-5;
    position: relative;
    transition: transform 0.2s ease;

    &--dragging {
      transition: none;
    }
  }

  &__remove-swipe {
    align-items: center;
    background-color: $color-neutral-highest;
    border: none;
    color: $color-white;
    cursor: pointer;
    display: flex;
    font-size: 22px;
    inset: 0 0 0 auto;
    justify-content: center;
    position: absolute;
  }

  // Fixed square slot: a wide logo is contained in it instead of stretching the row.
  &__select .network-badge {
    --network-badge-height: 40px;

    justify-content: center;
    width: 40px;

    &__logo {
      max-width: 100%;
    }
  }

  &__select {
    align-items: center;
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    flex: 1;
    min-width: 0;
    padding: 0;
    text-align: left;
  }

  &__item {
    flex: 1;
    min-width: 0;

    .list-item__label {
      -webkit-box-orient: vertical;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  &__displayed-label {
    color: $color-text-neutral;
    font: $label-small;
    letter-spacing: $label-small-spacing;
  }

  &__check {
    color: $color-primary;
    font-size: 24px;
  }

  &__notifications {
    align-items: center;
    color: $color-neutral-higher;
    display: flex;
    font-size: 20px;
    gap: $spacing-2;
    margin-right: $spacing-5;
  }

  &__remove {
    margin-right: $spacing-4;
    position: relative;

    // Widens the tap target past the button's visual bounds without affecting the layout.
    &::after {
      content: '';
      inset: -8px;
      position: absolute;
    }
  }

  &__add {
    background: none;
    border: none;
    cursor: pointer;
    display: block;
    padding: 0;
    text-align: left;
    width: 100%;
  }
}
</style>
